import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, BehaviorSubject, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { BaseAPIService } from '../baseapi/baseapiservice';
import { HttpClient } from '@angular/common/http';
import { User } from '../../models/user';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';

const REQUIRED_PERMISSION = 'UseConfigurator';
const UNAUTHORIZED_MESSAGE = 'You do not have access to this site';
const PROVISIONS_USERNAME_KEY = 'provisionsUserName';

@Injectable()
export class UserService extends BaseAPIService {

    private user: User | null = null;
    private userupdate = new ReplaySubject<User>(1);
    private verifyInProgress$: Observable<boolean> | null = null;
    private loadingSubject = new BehaviorSubject<boolean>(false);

    readonly loading$ = this.loadingSubject.asObservable();

    constructor(private http: HttpClient, private router: Router) {
        super();
    }

    getUserUpdates(): Observable<User> {
        return this.userupdate.asObservable();
    }

    verifyClientAccess(): Observable<boolean> {
        if (this.user !== null) {
            return of(true);
        }

        if (this.verifyInProgress$ !== null) {
            return this.verifyInProgress$;
        }

        this.loadingSubject.next(true);

        this.verifyInProgress$ = this.getLoggedInUser().pipe(
            tap(response => {
                const hasAccess = response.permissions.includes(REQUIRED_PERMISSION);
                if (hasAccess) {
                    this.user = response;
                    this.user.provisionsUserName = this.getInitialProvisionLibrary(response);
                    this.userupdate.next(this.user);
                } else {
                    this.navigateToUnauthorized();
                }           
            }),
            map(response => this.user === response),
            catchError(() => {
                this.navigateToUnauthorized();
                return of(false);
            }),
            tap(() => {
                this.verifyInProgress$ = null;
                this.loadingSubject.next(false);
            }),
            shareReplay(1)
        );

        return this.verifyInProgress$;
    }

    getLoggedInUser(): Observable<User> {
        return this.http.get<User>(`${environment.usersv2Url}/loggedInUser`).pipe(
            catchError(this.handleError)
        );
    }

    setProvisionsUserName(name: string): void {
        localStorage.setItem(PROVISIONS_USERNAME_KEY, name);
        if (this.user !== null) {
            this.user.provisionsUserName = name;
        }
    }

    private getInitialProvisionLibrary(user: User): string {
        // Keep the library selected on the previous visit.
        const savedLibrary = localStorage.getItem(PROVISIONS_USERNAME_KEY);
        if (savedLibrary && savedLibrary.trim()) {
            return savedLibrary.trim();
        }

        if (typeof user.xdsUserId === 'string' && user.xdsUserId.trim()) {
            return user.xdsUserId.trim();
        }

        if (typeof user.provisionsUserName === 'string') {
            return user.provisionsUserName.trim();
        }

        return '';
    }

    private navigateToUnauthorized(): void {
        this.router.navigate(['unauthorized'], { queryParams: { message: UNAUTHORIZED_MESSAGE } });
    }
}
